import { useEffect, useRef, useState } from "react";
import { R } from "../resources";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
import "../styles/page-about.css";

const CTA_LABEL = "Begin a partnership";




/* ──────── nav ──────── */



/* ──────── about hero ──────── */

function AboutHero() {
  return (
    <section className="page-hero" id="top" data-screen-label="01 About hero">
      <div className="wrap">
        <span className="eyebrow">About Upside</span>
        <h1 className="page-hero-h">Meet the team behind <span className="accent ink">high-performance marketing.</span></h1>
        <p className="page-hero-sub">One operation, four continents, every time zone. While one market sleeps, another is reading the data, refreshing creative, and scaling your campaigns. Here’s who runs it, and how they’re built to work.</p>
      </div>
    </section>);

}

/* ──────── about timeline ──────── */

function Timeline() {
  const ref = useRef<HTMLElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const startYear = 2009;
  const endYear = new Date().getFullYear();
  useEffect(() => {
    const sec = ref.current;
    if (!sec) return;
    let done = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting && !done) {
          done = true;
          sec.classList.add("in");
          const fill = sec.querySelector<HTMLElement>(".timeline-fill");
          const marker = sec.querySelector<HTMLElement>(".timeline-marker");
          requestAnimationFrame(() => { if (fill) fill.style.width = "100%"; if (marker) marker.style.left = "100%"; });
          const dur = 2600;
          const t0 = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            const yr = Math.round(startYear + (endYear - startYear) * eased);
            if (bubbleRef.current) bubbleRef.current.textContent = String(yr);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    io.observe(sec);
    return () => io.disconnect();
  }, [endYear]);
  return (
    <section className="timeline-sec" ref={ref} data-screen-label="02 Timeline">
      <div className="wrap">
        <div className="timeline-track-wrap">
          <div className="timeline-track">
            <div className="timeline-fill"></div>
            <div className="timeline-marker">
              <div className="bubble" ref={bubbleRef}>{startYear}</div>
              <div className="dot"></div>
            </div>
          </div>
          <div className="timeline-ends">
            <div className="tl-end">
              <span className="tl-yr">{startYear} — Founded</span>
              <span className="tl-cap">From an idea...</span>
            </div>
            <div className="tl-end tl-end-right">
              <span className="tl-yr">{endYear} — Today</span>
              <span className="tl-cap">...to a global operation.</span>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ──────── about one team ──────── */

function OneTeam() {
  return (
    <section className="oneteam" data-screen-label="03 One team">
      <div className="wrap">
        <div className="oneteam-grid">
          <div>
            <span className="eyebrow">Global Teamwork</span>
            <h2 style={{ marginTop: 18 }}>One team, <span className="accent ink">every time zone.</span></h2>
          </div>
          <div className="oneteam-body">
            <p>We don’t have offices scattered across time zones so much as one operation that never fully stops. Australia, the US, Europe, and Asia. The work follows the sun, and the optimization runs around the clock.</p>
            <p>That footprint isn’t only about scale. It’s about where the work gets done best. Strategy, media buying across Meta, Google, and TikTok, and creative direction run out of the US, UK, and Australia. Our technical specialists and data analysts sit in Asia, keeping the proprietary systems running and validating leads in real time.</p>
            <p>We’re selective about who joins. We don’t hire off a resume; we hire people who have already done the work and chose to leave the agency-as-usual world for a model where performance is the only thing that gets measured. From the developers building our AI to the CRO specialists tuning conversion, every one of them is a specialist, not a generalist minding an account.</p>
            <p>That mix only works because the teams work in lockstep. The people writing the hook and the people building the delivery system behind it talk constantly, so the ad a prospect clicks matches the logic of what happens next. We don’t manage budgets. We manage outcomes, and we treat your CPL targets as our own.</p>
            <blockquote className="oneteam-quote">
              <p>“Most agencies hire account managers to talk to you. <span className="accent">We hire specialists to deliver for you.</span>”</p>
            </blockquote>
          </div>
        </div>
      </div>
    </section>);

}

/* ──────── about leaders (split parallax) ──────── */

function Leaders() {
  return (
    <section className="leaders" data-screen-label="04 Leadership">
      <div className="wrap">
        <div className="leader img-right">
          <div className="leader-photo lee"><img src={R.lee} alt="Lee Bush" /></div>
          <div className="leader-content">
            <span className="eyebrow">Leadership</span>
            <span className="leader-role">Lee Bush — Founder &amp; CEO</span>
            <h2>Leadership driven by <span className="accent ink">performance.</span></h2>
            <div className="leader-cols">
              <p>Lee started Upside in 2009 to close a gap she kept running into: the distance between serious IT infrastructure and high-growth digital marketing. Most marketing people couldn’t build the systems, and most systems people couldn’t market. She could do both.</p>
              <p>Her bet was straightforward, and at the time against the grain. Build an offering that lets clients scale profitably alongside the marketing they’re already running, instead of fighting over the same slice of the pie. That model became the foundation of the business.</p>
              <blockquote className="leader-quote">
                <p>“We’re small enough to listen, but experienced enough to make a difference through the application of great digital marketing and technology. A great team, great experience, and great results are the non-negotiables of our success.”</p>
                <cite>Lee Bush</cite>
              </blockquote>
              <p>Her IT background is the reason our real-time validation systems work the way they do. She understood early that marketing had stopped being only about the visuals, that data integrity, technical scale, and precision were about to matter just as much, and she built for it before most agencies saw it coming.</p>
              <p>Today she leads the global team across Australia, the US, Europe, and Asia with one principle holding it together: shared risk. By putting our own capital into client results instead of billing a retainer and walking away, Lee has made winning the standard rather than the goal.</p>
              <p>What that means for you: you’re not hiring a vendor. You’re working with a founder who has spent more than fifteen years refining lead quality and unit economics into a single outcome: profitable scale that holds.</p>
            </div>
          </div>
        </div>
        <div className="leader">
          <div className="leader-photo ryan"><img src={R.ryan} alt="Ryan Beck" /></div>
          <div className="leader-content">
            <span className="eyebrow">Leadership</span>
            <span className="leader-role">Ryan Beck — General Manager &amp; Partner</span>
            <h2>Leadership driven by <span className="accent ink">results.</span></h2>
            <div className="leader-cols">
              <p>Ryan took over as General Manager in 2013, back when the business was still UM Direct, and has run its operations and growth ever since. As a partner, he owns the company’s core objectives, and above everything, he keeps the people paying us at the front of every decision.</p>
              <p>He runs on two convictions: that long-term success is built on client relationships that don’t wobble, and on a team that keeps getting better at what it does.</p>
              <blockquote className="leader-quote">
                <p>“I’m motivated by a genuine passion for unleashing the potential that lies within the lead generation and digital marketing industries. We don’t just find leads; we find solutions.”</p>
                <cite>Ryan Beck</cite>
              </blockquote>
              <p>His read on the market isn’t only internal. Ryan is a recognized voice in Australian digital, having spoken at and MC’d major industry events, the Adtech Conference in Sydney and the National eCommerce Conferences in Melbourne among them. It keeps him close to where the market is actually heading.</p>
              <p>That perspective matters, because his job is to steer the shift from traditional advertising into the performance model we run now. As we scale past $50M in annual media spend, Ryan is the one making sure clients’ brands are handled with care and their sales targets get hit.</p>
              <p>If you’re weighing up scaling your business, he’s the strategist you want across the table. He’s good at finding the opportunities buried in a sales funnel that nobody’s noticed yet, and pointing our global resources straight at them.</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ──────── about partnership quote (centered) ──────── */

function PartnershipQuote() {
  return (
    <section className="ai-statement" data-screen-label="05 Partnership">
      <div className="wrap">
        <div className="ai-quote-wrap">
          <span className="eyebrow">The power of the partnership</span>
          <h2 className="ai-quote"><span className="accent">Lee</span> builds the engine and keeps it ahead of the field. <span className="accent">Ryan</span> keeps it pointed at your business.</h2>
          <p className="ai-sub">What makes the model work is the fit between the two of them. One makes sure the system is the best it can be; the other makes sure it’s aimed at the outcomes you actually care about. Between them, you get a team that behaves less like a vendor and more like an extension of your own marketing department.</p>
        </div>
      </div>
    </section>);

}

/* ──────── about crew marquee ──────── */

function Crew() {
  const vpRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const crew = [
    ["Barak", "Account Director", "account-director-bb"], 
    ["Jo", "Account Director", "account-director-ja"], 
    ["Samir", "Head of Paid Search", "head-of-paid-search"], 
    ["Darwin", "Head of Paid Social", "head-of-paid-social"], 
    ["Yannick", "Head of Native Ads", "head-of-native-ads"], 
    ["Glody", "Head of Product & Technology", "head-of-product-tech"], 
    ["Kate", "Product & Data Specialist", "product-data-specialist"], 
    ["Jeoff", "Digital Experimentation Specialist", "digital-experimentation-specialist"], 
    ["Sergio", "Creative Strategist", "creative-strategist-sc"], 
    ["Kim", "Creative Strategist", "creative-strategist-ka"], 
    ["Dio", "Head of Development", "head-of-development"], 
    ["Mildred", "Full-Stack Developer", "full-stack-developer"], 
    ["Angelo", "QA Automation Engineer", "qa-automation-engineer"],
    ["Fred", "Paid Search Operations Coordinator", "paid-search-operations-coordinator"],
    ["Angela", "Financial Controller", "financial-controller"]
  ];
  useEffect(() => {
    const track = trackRef.current;
    const vp = vpRef.current;
    if (!track || !vp) return;
    let offset = 0;
    let paused = false;
    let raf;
    const speed = 0.5;
    const step = () => {
      if (!paused) {
        offset -= speed;
        const first = track.firstElementChild;
        if (first) {
          const fw = first.getBoundingClientRect().width + 28;
          if (-offset >= fw) { offset += fw; track.appendChild(first); }
        }
        track.style.transform = "translateX(" + offset + "px)";
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    const enter = () => { paused = true; };
    const leave = () => { paused = false; };
    vp.addEventListener("mouseenter", enter);
    vp.addEventListener("mouseleave", leave);
    return () => { cancelAnimationFrame(raf); vp.removeEventListener("mouseenter", enter); vp.removeEventListener("mouseleave", leave); };
  }, []);
  return (
    <section className="crew" data-screen-label="06 Crew">
      <div className="wrap crew-head">
        <span className="eyebrow">The crew</span>
        <h2 style={{ marginTop: 18 }}>Meet the rest of <span className="accent ink">the crew.</span></h2>
      </div>
      <div className="crew-viewport" ref={vpRef}>
        <div className="crew-track" ref={trackRef}>
          {crew.map((m, i) => (
          <div className="crew-card" key={i}>
              <div className="crew-photo">
                <img src={"/assets/img/team/" + m[2] + ".jpg"} alt={m[0] + " — " + m[1]} loading="lazy" />
              </div>
              <div className="crew-name">{m[0]}</div>
              <div className="crew-role">{m[1]}</div>
            </div>
          ))}
        </div>
      </div>
    </section>);

}

/* ──────── about closing ──────── */

function AboutClosing({ ctaLabel, onCta }) {
  return (
    <section className="closing" data-tone="dark" data-screen-label="07 Closing CTA">
      <div className="wrap">
        <div className="closing-grid">
          <div>
            <span className="eyebrow">Next step</span>
          </div>
          <div>
            <h2>Start the <em>conversation.</em></h2>
            <p className="closing-side" style={{ marginTop: 28 }}>
              The right partnership starts with the right fit. Tell us where you’re trying to take the business, and we’ll work out together whether we’re the team to get you there.
            </p>
            <div className="closing-foot">
              <button className="link-btn-primary" onClick={onCta}>
                {ctaLabel} <span className="arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ──────── footer ──────── */

function AboutPage() {
  const [csOpen, setCsOpen] = useState(false);

  useEffect(() => {document.body.setAttribute("data-density", "standard");}, []);

  const openCs = (e) => {if (e) e.preventDefault();setCsOpen(true);};

  return (
    <div className="page page-about">
      <Nav onCta={openCs} ctaLabel={CTA_LABEL} />
      <AboutHero />
      <Timeline />
      <OneTeam />
      <Leaders />
      <PartnershipQuote />
      <Crew />
      <AboutClosing ctaLabel={CTA_LABEL} onCta={openCs} />
      <Footer onContact={openCs} />

      <ContactModal open={csOpen} onClose={() => setCsOpen(false)} />
    </div>);

}

export default AboutPage;
